import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Search, Play, Clock, Loader2,
  Plus, Trash2, ChevronDown, BookOpen, Award, Sparkles, X,
  ChevronUp, GripVertical, Film,
} from "lucide-react";
import { api } from "../../api/client";
import { toast } from "sonner";

const CATEGORIES = ["Cybersecurity", "Close Protection", "Travel Safety", "Tactical", "Risk Management", "Maritime"];
const LEVELS     = ["Beginner", "Intermediate", "Advanced"];
const STEPS      = ["Select Videos", "Course Details", "Quiz", "Publish"];

function fmtSecs(s) {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

const inp = "w-full bg-[#0E0F12] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#CFFF50]/40 placeholder-gray-600";

// ── Step 1: Multi-video picker ───────────────────────────────────────────────
function StepVideos({ selected, onToggle, onReorder, onRename, onDescribe }) {
  const [query,           setQuery]           = useState("");
  const [results,         setResults]         = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [collections,     setCollections]     = useState([]);
  const [activeCol,       setActiveCol]       = useState("");
  const debounce  = useRef(null);
  const activeRef = useRef("");

  const search = useCallback(async (q) => {
    setLoading(true); setError(null);
    try {
      const colParam = activeRef.current ? `&collection_id=${encodeURIComponent(activeRef.current)}` : "";
      const data = await api.get(`/admin/bunny/videos?q=${encodeURIComponent(q)}${colParam}`);
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message?.includes("not configured")
        ? "Bunny.net not configured — add BUNNY_API_KEY and BUNNY_LIBRARY_ID to your backend .env"
        : "Could not reach Bunny.net");
      setResults([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/admin/bunny/collections").then((cols) => {
      if (Array.isArray(cols) && cols.length) setCollections(cols);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(query), 400);
    return () => clearTimeout(debounce.current);
  }, [query, search]);

  const switchCollection = (id) => {
    setActiveCol(id);
    activeRef.current = id;
    clearTimeout(debounce.current);
    search(query);
  };

  const move = (i, dir) => {
    const next = [...selected];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onReorder(next);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 min-h-0">

      {/* Left — Bunny library */}
      <div className="flex-1 min-w-0 space-y-4">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-white">Select videos from Bunny</h2>
          <p className="text-gray-500 text-sm mt-1">Click videos to add them as lessons. Select as many as you need.</p>
        </div>

        {/* Collection tabs */}
        {collections.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => switchCollection("")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeCol === "" ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/[0.06] text-gray-400 hover:text-white"}`}
            >
              All videos
            </button>
            {collections.map((c) => (
              <button
                key={c.id}
                onClick={() => switchCollection(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeCol === c.id ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/[0.06] text-gray-400 hover:text-white"}`}
              >
                {c.name} <span className="opacity-60">({c.video_count})</span>
              </button>
            ))}
          </div>
        )}

        <div className="relative">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            autoFocus value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search videos…"
            className={`${inp} pl-10`}
          />
          {loading && <Loader2 size={14} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-gray-500" />}
        </div>

        {error && <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

        {!error && (results.length === 0 && !loading ? (
          <div className="text-center py-16 text-gray-600">
            <Film size={32} className="mx-auto mb-3 opacity-40" />
            <p>{query ? "No videos match your search." : "Type to search your Bunny library."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {results.map((v) => {
              const isSelected = selected.some((s) => s.id === v.id);
              return (
                <button
                  key={v.id}
                  onClick={() => onToggle(v)}
                  className={`text-left rounded-2xl overflow-hidden border transition-all group ${
                    isSelected
                      ? "border-[#CFFF50]/60 ring-2 ring-[#CFFF50]/20"
                      : "border-white/[0.08] hover:border-white/20"
                  }`}
                >
                  <div className="aspect-video bg-black relative overflow-hidden">
                    {v.thumbnail ? (
                      <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e)=>{e.target.style.display="none"}} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#111318]">
                        <Play size={20} className="text-gray-600" />
                      </div>
                    )}
                    {v.duration > 0 && (
                      <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
                        {fmtSecs(v.duration)}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 bg-[#CFFF50]/20 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-[#CFFF50] flex items-center justify-center">
                          <Check size={16} className="text-[#0E0F12]" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold text-gray-300 line-clamp-2 leading-snug">{v.title || "Untitled"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Right — selected lesson list */}
      <div className="lg:w-72 shrink-0">
        <div className="sticky top-0">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white">
              {selected.length === 0 ? "No videos selected" : `${selected.length} lesson${selected.length !== 1 ? "s" : ""} selected`}
            </h3>
          </div>

          {selected.length === 0 ? (
            <div className="border-2 border-dashed border-white/[0.06] rounded-2xl p-6 text-center">
              <Film size={24} className="mx-auto mb-2 text-gray-700" />
              <p className="text-xs text-gray-600">Click videos on the left to add them here</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {selected.map((v, i) => (
                <div key={v.id} className="bg-[#111318] border border-white/[0.08] rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    {/* Reorder */}
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-700 hover:text-gray-400 disabled:opacity-20 transition-colors">
                        <ChevronUp size={12} />
                      </button>
                      <button onClick={() => move(i, 1)} disabled={i === selected.length - 1} className="text-gray-700 hover:text-gray-400 disabled:opacity-20 transition-colors">
                        <ChevronDown size={12} />
                      </button>
                    </div>
                    {/* Thumbnail */}
                    <div className="w-12 h-8 rounded-lg overflow-hidden bg-black shrink-0">
                      {v.thumbnail
                        ? <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center"><Play size={10} className="text-gray-600" /></div>
                      }
                    </div>
                    {/* Duration */}
                    {v.duration > 0 && (
                      <span className="text-[10px] text-gray-600 font-mono shrink-0">{fmtSecs(v.duration)}</span>
                    )}
                    {/* Remove */}
                    <button onClick={() => onToggle(v)} className="ml-auto text-gray-600 hover:text-red-400 transition-colors shrink-0">
                      <X size={12} />
                    </button>
                  </div>
                  {/* Editable lesson title */}
                  <input
                    value={v.lessonTitle}
                    onChange={(e) => onRename(v.id, e.target.value)}
                    placeholder="Lesson title…"
                    className="w-full bg-[#0a0b0e] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#CFFF50]/30 placeholder-gray-700"
                  />
                  {/* Lesson description */}
                  <textarea
                    value={v.lessonDescription || ""}
                    onChange={(e) => onDescribe(v.id, e.target.value)}
                    placeholder="What will students learn in this lesson? (optional)"
                    rows={2}
                    className="w-full bg-[#0a0b0e] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-xs text-gray-400 focus:outline-none focus:border-[#CFFF50]/30 placeholder-gray-700 resize-none"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Course Details ───────────────────────────────────────────────────
function StepDetails({ details, onChange, videos }) {
  const set = (k, v) => onChange({ ...details, [k]: v });
  const [topicInput, setTopicInput] = React.useState("");
  const addTopic = () => {
    const t = topicInput.trim();
    if (!t) return;
    set("topics", [...(details.topics || []), t]);
    setTopicInput("");
  };
  const removeTopic = (i) => set("topics", (details.topics || []).filter((_, j) => j !== i));
  const bunnyDesc = videos?.[0]?.description || "";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight text-white">Course details</h2>
        <p className="text-gray-500 text-sm mt-1">Fill in the information students will see on the course page.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lg:col-span-2">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Course Title *</label>
          <input className={inp} placeholder="e.g. Close Protection Operations — Foundations" value={details.title} onChange={(e) => set("title", e.target.value)} />
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Description *</label>
            {bunnyDesc && !details.description && (
              <button type="button" onClick={() => set("description", bunnyDesc)}
                className="text-xs text-[#CFFF50] hover:underline font-semibold">
                ↓ Import from Bunny
              </button>
            )}
          </div>
          <textarea className={`${inp} resize-y`} rows={4} placeholder="What will students learn? Who is this for?" value={details.description} onChange={(e) => set("description", e.target.value)} />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Category *</label>
          <div className="relative">
            <select className={`${inp} appearance-none pr-10`} value={details.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">Select category…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Level *</label>
          <div className="relative">
            <select className={`${inp} appearance-none pr-10`} value={details.level} onChange={(e) => set("level", e.target.value)}>
              <option value="">Select level…</option>
              {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Price (£) *</label>
          <input type="number" min={0} step={0.01} className={inp} placeholder="299.00" value={details.price} onChange={(e) => set("price", e.target.value)} />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Original Price (£)</label>
          <input type="number" min={0} step={0.01} className={inp} placeholder="599.00" value={details.original_price} onChange={(e) => set("original_price", e.target.value)} />
        </div>

        <div className="lg:col-span-2">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Thumbnail URL <span className="text-gray-600 normal-case font-normal">(leave blank to use first video thumbnail)</span></label>
          <input className={inp} placeholder="https://…" value={details.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} />
          {details.thumbnail && (
            <img src={details.thumbnail} alt="thumbnail" className="mt-2 w-32 h-20 object-cover rounded-xl border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Instructor Name</label>
          <input className={inp} placeholder="Nige Thomas" value={details.instructor_name} onChange={(e) => set("instructor_name", e.target.value)} />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Instructor Title</label>
          <input className={inp} placeholder="BMG Course Instructor" value={details.instructor_title} onChange={(e) => set("instructor_title", e.target.value)} />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">Duration (hours)</label>
          <input type="number" min={0} step={0.5} className={inp} placeholder="2.5" value={details.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} />
        </div>

        <div className="lg:col-span-2">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 block">What students will learn <span className="text-gray-600 normal-case font-normal">(shown on course page)</span></label>
          <div className="flex gap-2 mb-3">
            <input
              className={inp}
              placeholder="e.g. Identify threat indicators in a public order situation"
              value={topicInput}
              onChange={(e) => setTopicInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
            />
            <button type="button" onClick={addTopic}
              className="shrink-0 px-4 py-2 rounded-xl bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors">
              <Plus size={14} />
            </button>
          </div>
          {(details.topics || []).length > 0 && (
            <ul className="space-y-2">
              {(details.topics || []).map((t, i) => (
                <li key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.07] rounded-xl px-4 py-2.5 text-sm text-gray-300">
                  <Check size={13} className="text-[#CFFF50] shrink-0" />
                  <span className="flex-1">{t}</span>
                  <button type="button" onClick={() => removeTopic(i)} className="text-gray-600 hover:text-red-400 transition-colors"><X size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Per-lesson Quiz ───────────────────────────────────────────────────
function LessonQuizEditor({ questions, onChange }) {
  const addQ = () => onChange([...questions, {
    id: `q_${Date.now()}`, text: "", options: ["", ""], correct_indices: [0], multi: false,
  }]);
  const removeQ = (i) => onChange(questions.filter((_, j) => j !== i));
  const setQ    = (i, patch) => onChange(questions.map((q, j) => j === i ? { ...q, ...patch } : q));
  const setOpt  = (qi, oi, val) => { const opts = [...questions[qi].options]; opts[oi] = val; setQ(qi, { options: opts }); };
  const addOpt  = (qi) => setQ(qi, { options: [...questions[qi].options, ""] });
  const removeOpt = (qi, oi) => {
    const q = questions[qi];
    if (q.options.length <= 2) return;
    const opts = q.options.filter((_, j) => j !== oi);
    const cis  = (q.correct_indices || [0]).filter(c => c !== oi).map(c => c > oi ? c - 1 : c);
    setQ(qi, { options: opts, correct_indices: cis.length ? cis : [0] });
  };
  const toggleCorrect = (qi, oi) => {
    const q = questions[qi];
    if (!q.multi) { setQ(qi, { correct_indices: [oi] }); return; }
    const cis  = q.correct_indices || [];
    const next = cis.includes(oi) ? cis.filter(c => c !== oi) : [...cis, oi];
    setQ(qi, { correct_indices: next.length ? next : [oi] });
  };

  return (
    <div className="space-y-3 mt-3">
      {questions.map((q, qi) => (
        <div key={q.id} className="bg-[#0a0b0e] border border-white/[0.08] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Question {qi + 1}</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setQ(qi, { multi: !q.multi })}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
                  q.multi ? "bg-[#7C5CFF]/20 text-[#7C5CFF]" : "bg-white/[0.05] text-gray-500 hover:text-gray-300"
                }`}>
                {q.multi ? "Multi-answer" : "Single answer"}
              </button>
              <button onClick={() => removeQ(qi)} className="text-gray-700 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
            </div>
          </div>

          <input className={inp} placeholder="Type the question…" value={q.text} onChange={(e) => setQ(qi, { text: e.target.value })} />

          <div className="space-y-1.5">
            {q.options.map((opt, oi) => {
              const isCorrect = (q.correct_indices || [0]).includes(oi);
              return (
                <div key={oi} className="flex items-center gap-2">
                  <div onClick={() => toggleCorrect(qi, oi)}
                    className={`flex-1 flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                      isCorrect ? "border-[#CFFF50] bg-[#CFFF50]/10" : "border-white/[0.06] hover:border-white/15"
                    }`}>
                    {q.multi ? (
                      <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                        isCorrect ? "bg-[#CFFF50] border-[#CFFF50]" : "border-gray-600"
                      }`}>
                        {isCorrect && <Check size={9} color="#0E0F12" strokeWidth={3} />}
                      </span>
                    ) : (
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${
                        isCorrect ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/10 text-gray-500"
                      }`}>
                        {String.fromCharCode(65 + oi)}
                      </span>
                    )}
                    <input
                      className="flex-1 bg-transparent text-xs text-white focus:outline-none placeholder-gray-600"
                      placeholder={`Option ${String.fromCharCode(65 + oi)}…`} value={opt}
                      onChange={(e) => { e.stopPropagation(); setOpt(qi, oi, e.target.value); }}
                      onClick={(e) => e.stopPropagation()} />
                    {isCorrect && !q.multi && <span className="text-[9px] font-black text-[#CFFF50] uppercase tracking-widest shrink-0">✓ Correct</span>}
                  </div>
                  {q.options.length > 2 && (
                    <button onClick={() => removeOpt(qi, oi)}
                      className="w-6 h-6 rounded flex items-center justify-center text-gray-700 hover:text-red-400 transition-colors shrink-0">
                      <X size={10} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button onClick={() => addOpt(qi)}
            className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors flex items-center gap-1.5">
            <Plus size={10} /> Add option
          </button>
        </div>
      ))}
      <button onClick={addQ} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-white/[0.1] text-xs text-gray-500 hover:text-white hover:border-white/20 transition-colors">
        <Plus size={12} /> Add question
      </button>
    </div>
  );
}

function StepQuiz({ videos, lessonQuizzes, onChange }) {
  const [open, setOpen] = React.useState({});

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight text-white">Add quizzes <span className="text-gray-600 font-normal text-lg">(optional)</span></h2>
        <p className="text-gray-500 text-sm mt-1">Add questions per lesson. Students must score 80% to advance. Skip any you don't need yet.</p>
      </div>
      <div className="space-y-3">
        {videos.map((v, i) => {
          const qs = lessonQuizzes[v.id] || [];
          const isOpen = open[v.id];
          return (
            <div key={v.id} className="bg-[#111318] border border-white/[0.08] rounded-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpen(o => ({ ...o, [v.id]: !o[v.id] }))}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-[#0a0b0e] flex items-center justify-center shrink-0 text-xs font-bold text-gray-500">{i + 1}</div>
                <span className="flex-1 text-sm font-semibold text-white truncate">{v.lessonTitle || v.title}</span>
                {qs.length > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#CFFF50]/15 text-[#CFFF50]">{qs.length}Q</span>
                )}
                <ChevronDown size={14} className={`text-gray-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-white/[0.06]">
                  <LessonQuizEditor
                    questions={qs}
                    onChange={(newQs) => onChange({ ...lessonQuizzes, [v.id]: newQs })}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 4: Publish preview ───────────────────────────────────────────────────
function StepPublish({ videos, details, lessonQuizzes }) {
  const questions = Object.values(lessonQuizzes).flat();
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight text-white">Ready to publish</h2>
        <p className="text-gray-500 text-sm mt-1">Review your course before it goes live.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Course info card */}
        <div className="bg-[#111318] border border-white/[0.08] rounded-2xl p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-gray-600 font-bold">Course Info</p>
          <p className="font-bold text-white text-lg leading-tight">{details.title}</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><p className="text-xs text-gray-500">Category</p><p className="text-white">{details.category}</p></div>
            <div><p className="text-xs text-gray-500">Level</p><p className="text-white">{details.level}</p></div>
            <div><p className="text-xs text-gray-500">Price</p><p className="text-white">£{details.price}</p></div>
            <div><p className="text-xs text-gray-500">Lessons</p><p className="text-white">{videos.length}</p></div>
          </div>
        </div>

        {/* Quiz card */}
        <div className="bg-[#111318] border border-white/[0.08] rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-widest text-gray-600 font-bold mb-3">Quiz</p>
          {questions.length === 0
            ? <p className="text-sm text-gray-500">No quiz attached.</p>
            : <p className="text-sm text-white">{questions.length} question{questions.length !== 1 ? "s" : ""}</p>
          }
        </div>
      </div>

      {/* Lesson list */}
      <div className="bg-[#111318] border border-white/[0.08] rounded-2xl p-5">
        <p className="text-[10px] uppercase tracking-widest text-gray-600 font-bold mb-3">Lessons — Module 1</p>
        <div className="space-y-2">
          {videos.map((v, i) => (
            <div key={v.id} className="flex items-center gap-3 py-2 border-b border-white/[0.05] last:border-0">
              <span className="text-xs text-gray-600 w-5 shrink-0 text-right">{i + 1}</span>
              <div className="w-10 h-7 rounded-lg overflow-hidden bg-black shrink-0">
                {v.thumbnail
                  ? <img src={v.thumbnail} alt={v.lessonTitle} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center"><Play size={10} className="text-gray-600" /></div>
                }
              </div>
              <p className="text-sm text-white flex-1 truncate">{v.lessonTitle}</p>
              {v.duration > 0 && <span className="text-xs text-gray-600 font-mono shrink-0">{fmtSecs(v.duration)}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-3 bg-[#CFFF50]/5 border border-[#CFFF50]/20 rounded-2xl p-4">
        <Sparkles size={16} className="text-[#CFFF50] shrink-0 mt-0.5" />
        <p className="text-sm text-gray-300">Course goes live immediately. Add extra content, more modules, and assessments any time in the Course Studio.</p>
      </div>
    </div>
  );
}

// ── Main Wizard ──────────────────────────────────────────────────────────────
export default function CourseWizard() {
  const navigate = useNavigate();
  const [step,      setStep]      = useState(0);
  const [videos,    setVideos]    = useState([]); // { ...bunnyVideo, lessonTitle }
  const [details,   setDetails]   = useState({
    title: "", description: "", category: "", level: "",
    price: "", original_price: "", thumbnail: "",
    instructor_name: "Nige Thomas", instructor_title: "BMG Course Instructor",
    instructor_avatar: "", duration_hours: "", topics: [],
  });
  const [lessonQuizzes, setLessonQuizzes] = useState({}); // videoId → questions[]
  const [publishing, setPublishing] = useState(false);

  const toggleVideo = (v) => {
    setVideos((prev) => {
      const exists = prev.find((s) => s.id === v.id);
      if (exists) return prev.filter((s) => s.id !== v.id);
      return [...prev, { ...v, lessonTitle: v.title || "Untitled", lessonDescription: "" }];
    });
  };

  const renameLesson = (id, title) => {
    setVideos((prev) => prev.map((v) => v.id === id ? { ...v, lessonTitle: title } : v));
  };

  const describeLesson = (id, description) => {
    setVideos((prev) => prev.map((v) => v.id === id ? { ...v, lessonDescription: description } : v));
  };

  const canAdvance = () => {
    if (step === 0) return videos.length > 0;
    if (step === 1) return details.title && details.description && details.category && details.level && details.price;
    return true;
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const thumb = details.thumbnail || videos[0]?.thumbnail || "";

      const course = await api.post("/admin/courses", {
        title:            details.title,
        description:      details.description,
        category:         details.category,
        level:            details.level,
        price:            parseFloat(details.price) || 0,
        original_price:   parseFloat(details.original_price) || parseFloat(details.price) * 2 || 0,
        thumbnail:        thumb,
        cover:            thumb,
        instructor_name:  details.instructor_name,
        instructor_title: details.instructor_title,
        instructor_avatar: details.instructor_avatar || "",
        duration_hours:   parseFloat(details.duration_hours) || 0,
        topics:           details.topics || [],
      });

      // Build curriculum — all videos as lessons in one module
      await api.put(`/admin/courses/${course.id}/curriculum`, {
        stages: [{
          id:    `m_${Date.now().toString(36)}`,
          stage: "Module 1: Course Content",
          lessons: videos.map((v) => ({
            id:          `l_${v.id}`,
            title:       v.lessonTitle,
            description: v.lessonDescription || "",
            type:        "lesson",
            duration:    v.duration ? fmtSecs(v.duration) : "",
            video:       v.embed_url || "",
            blocks: [{
              id:   `b_${v.id}`,
              type: "video",
              data: {
                bunny_id:             v.id,
                url:                  v.embed_url || "",
                title:                v.lessonTitle,
                completion_threshold: 90,
              },
            }],
          })),
        }],
      });

      // Create per-lesson quizzes for lessons that have questions
      for (const v of videos) {
        const qs = lessonQuizzes[v.id] || [];
        const validQs = qs.filter((q) => q.text.trim() && q.options.every((o) => o.trim()));
        if (validQs.length > 0) {
          await api.put(`/admin/courses/${course.id}/lessons/l_${v.id}/quiz`, {
            title:     `${v.lessonTitle} Quiz`,
            questions: validQs,
            settings:  { pass_mark_percent: 80 },
          });
        }
      }

      toast.success(`Course "${details.title}" published with ${videos.length} lessons!`);
      navigate("/admin/courses");
    } catch (e) {
      console.error(e);
      toast.error("Publish failed — check your connection and try again");
    } finally {
      setPublishing(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E0F12] text-white flex flex-col">
      {/* Header */}
      <header className="h-14 shrink-0 flex items-center gap-4 px-6 border-b border-white/[0.08] bg-[#09090c]">
        <button onClick={() => navigate("/admin/courses")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors pr-4 border-r border-white/[0.08]">
          <ArrowLeft size={14} /> Courses
        </button>
        <BookOpen size={14} className="text-gray-600" />
        <span className="text-sm font-semibold text-white">New Course</span>
      </header>

      {/* Step indicator */}
      <div className="shrink-0 px-6 pt-6 pb-0 max-w-5xl mx-auto w-full">
        <div className="flex items-center gap-0">
          {STEPS.map((label, i) => {
            const done = i < step, current = i === step;
            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center gap-1.5 shrink-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    done ? "bg-[#CFFF50] text-[#0E0F12]" : current ? "bg-[#CFFF50]/20 border-2 border-[#CFFF50] text-[#CFFF50]" : "bg-white/[0.05] border border-white/10 text-gray-600"
                  }`}>
                    {done ? <Check size={14} /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap ${current ? "text-[#CFFF50]" : done ? "text-gray-400" : "text-gray-600"}`}>{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 mb-5 transition-colors ${i < step ? "bg-[#CFFF50]/40" : "bg-white/[0.06]"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {step === 0 && <StepVideos selected={videos} onToggle={toggleVideo} onReorder={setVideos} onRename={renameLesson} onDescribe={describeLesson} />}
          {step === 1 && <StepDetails details={details} onChange={setDetails} videos={videos} />}
          {step === 2 && <StepQuiz videos={videos} lessonQuizzes={lessonQuizzes} onChange={setLessonQuizzes} />}
          {step === 3 && <StepPublish videos={videos} details={details} lessonQuizzes={lessonQuizzes} />}
        </div>
      </main>

      {/* Footer */}
      <footer className="shrink-0 px-6 py-4 border-t border-white/[0.08] bg-[#09090c]">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : navigate("/admin/courses")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-sm text-gray-400 hover:text-white hover:border-white/30 transition-colors"
          >
            <ArrowLeft size={14} /> {step === 0 ? "Cancel" : "Back"}
          </button>

          <div className="flex items-center gap-3">
            {step === 0 && videos.length > 0 && (
              <span className="text-xs text-gray-500">{videos.length} video{videos.length !== 1 ? "s" : ""} selected</span>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canAdvance()}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {step === 2 ? (Object.values(lessonQuizzes).every(qs => !qs.length) ? "Skip & Review" : "Review") : "Next"}
                <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={publish}
                disabled={publishing}
                className="flex items-center gap-2 px-8 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-40"
              >
                {publishing ? <><Loader2 size={14} className="animate-spin" /> Publishing…</> : <><Sparkles size={14} /> Publish Course</>}
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
